import React, { Fragment, useMemo } from 'react';

import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Flex } from '@atlassian/eoc-flex';

import { SectionHeader, SectionInfo, SectionTitle, StyledChevronWrapper } from './styled';
import { type SectionHeaderProps } from './types';

export const Header = ({
	sectionId,
	isOpen,
	title,
	actions,
	info,
	alignTitleToContainer,
	alignHeaderItems = 'center',
	sectionDirection,
	onClick,
	className,
	headerRole,
}: SectionHeaderProps) => {
	const isCollapsible = useMemo(() => typeof isOpen !== 'undefined', [isOpen]);

	const ChevronWrapper = alignTitleToContainer ? StyledChevronWrapper : Fragment;
	const InfoWrapper = sectionDirection === 'column' ? Fragment : SectionInfo;

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			onClick?.();
		}
	};

	return (
		<Flex>
			<Flex flexDirection={sectionDirection} flexGrow={1}>
				<SectionHeader
					alignItems={alignHeaderItems}
					onClick={onClick}
					columnGap={alignTitleToContainer ? 0 : 1}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					isCollapsible={isCollapsible}
					aria-expanded={!headerRole ? isOpen : undefined}
					role={headerRole ?? 'button'}
					tabIndex={0}
					onKeyDown={handleKeyDown}
					id={`${sectionId}-header`}
					aria-controls={`${sectionId}-content`}
				>
					{isCollapsible && (
						<ChevronWrapper>
							{isOpen ? (
								<ChevronDownIcon
									color="currentColor"
									spacing="spacious"
									testId="opened-section-chevron"
									label=""
								/>
							) : (
								<ChevronRightIcon
									color="currentColor"
									spacing="spacious"
									testId="closed-section-chevron"
									label=""
								/>
							)}
						</ChevronWrapper>
					)}
					{typeof title === 'string' ? <SectionTitle>{title}</SectionTitle> : title}
				</SectionHeader>
				<InfoWrapper>{info}</InfoWrapper>
			</Flex>
			<Flex alignItems="center">{actions}</Flex>
		</Flex>
	);
};
